import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, CheckCircle } from "@phosphor-icons/react";

const REASON_PRESETS = ["Sakit", "Pulang", "Tugas", "Kegiatan Pondok", "Keperluan Keluarga", "Lainnya"];

// Warna default per status
const COLORS = {
  hadir: "bg-green-600 hover:bg-green-700",
  aktif_berbahasa: "bg-green-600 hover:bg-green-700",
  sehat: "bg-green-600 hover:bg-green-700",
  alfa: "bg-red-600 hover:bg-red-700",
  tidak_aktif_berbahasa: "bg-red-600 hover:bg-red-700",
  izin: "bg-amber-600 hover:bg-amber-700",
  izin_berobat: "bg-amber-600 hover:bg-amber-700",
  sakit: "bg-sky-600 hover:bg-sky-700",
  berhalangan: "bg-pink-600 hover:bg-pink-700",
};

const LABELS = {
  aktif_berbahasa: "Aktif",
  tidak_aktif_berbahasa: "Tdk Aktif",
  izin_berobat: "Izin Berobat",
  berhalangan: "Berhalangan",
};

const humanize = (s) => LABELS[s] || (s.charAt(0).toUpperCase() + s.slice(1));

/**
 * BulkAttendance — daftar santri otomatis per Kamar/Kelas/Halaqah,
 * tombol status dinamis per divisi. Izin buka input alasan.
 *
 * Props:
 *   endpoint    : "/absensi-divisi" | "/absensi/santri" | "/absensi-halaqah"
 *   basePayload : object di-merge ke setiap POST (mis. { divisi, kegiatan, tanggal, jam })
 *   statuses    : array status yang boleh dipilih untuk divisi ini
 *                 default: ["hadir", "alfa", "izin", "sakit"]
 *   izinKey     : status string yang memicu input alasan (default "izin")
 *   femaleOnlyStatuses : array status yang hanya boleh untuk santri putri (mis. ["berhalangan"])
 *   onDone      : callback setelah simpan sukses
 */
export default function BulkAttendance({
  endpoint,
  basePayload = {},
  statuses = ["hadir", "alfa", "izin", "sakit"],
  izinKey = "izin",
  femaleOnlyStatuses = ["berhalangan"],
  onDone,
}) {
  const [santriList, setSantriList] = useState([]);
  const [master, setMaster] = useState({ kamar: [], kelas: [], halaqah: [] });
  const [filter, setFilter] = useState({ kamar_id: "", kelas_id: "", halaqah_id: "" });
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [k, ka, h] = await Promise.all([
          api.get("/master/kelas"),
          api.get("/master/kamar"),
          api.get("/master/halaqah"),
        ]);
        setMaster({ kelas: k.data, kamar: ka.data, halaqah: h.data });
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    const p = { limit: 500, status: "aktif" };
    if (filter.kamar_id) p.kamar_id = filter.kamar_id;
    if (filter.kelas_id) p.kelas_id = filter.kelas_id;
    if (filter.halaqah_id) p.halaqah_id = filter.halaqah_id;
    api.get("/santri", { params: p }).then((r) => { setSantriList(r.data); setMarks({}); }).catch(() => setSantriList([]));
  }, [filter]);

  const setMark = (id, patch) => setMarks((m) => ({ ...m, [id]: { ...m[id], ...patch } }));
  const markAllHadir = () => {
    const first = statuses[0] || "hadir";
    const next = {};
    for (const s of santriList) next[s.id] = { ...(marks[s.id] || {}), status: first };
    setMarks(next);
  };

  const filledCount = useMemo(() => Object.values(marks).filter((m) => m?.status).length, [marks]);

  const submit = async () => {
    const rows = santriList.filter((s) => marks[s.id]?.status);
    if (rows.length === 0) return toast.error("Tandai minimal satu santri");
    for (const s of rows) {
      const m = marks[s.id];
      if (m.status === izinKey) {
        const alasanFinal = m.alasan_pick === "Lainnya" ? (m.alasan || "").trim() : (m.alasan_pick || "");
        if (!alasanFinal) return toast.error(`Isi alasan izin untuk ${s.nama}`);
      }
    }
    setSaving(true);
    let ok = 0, fail = 0;
    for (const s of rows) {
      const m = marks[s.id];
      const alasanFinal = m.status === izinKey
        ? (m.alasan_pick === "Lainnya" ? m.alasan.trim() : m.alasan_pick)
        : undefined;
      const payload = {
        ...basePayload,
        nis: s.nis,
        status: m.status,
        alasan: alasanFinal,
        keterangan: m.catatan || basePayload.keterangan,
        catatan: m.catatan,
      };
      try { await api.post(endpoint, payload); ok += 1; } catch (_) { fail += 1; }
    }
    setSaving(false);
    if (ok) toast.success(`Berhasil menyimpan ${ok} absensi${fail ? ` (${fail} gagal)` : ""}`);
    else toast.error("Gagal menyimpan absensi");
    setMarks({});
    onDone && onDone();
  };

  return (
    <div className="space-y-4">
      <div className="imsyaf-card p-4 grid md:grid-cols-4 gap-3">
        <div><label className="text-xs">Pilih Kamar</label>
          <select className="imsyaf-input" value={filter.kamar_id} onChange={(e) => setFilter({ kamar_id: e.target.value, kelas_id: "", halaqah_id: "" })} data-testid="bulk-kamar">
            <option value="">— Semua Kamar —</option>
            {master.kamar.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
          </select></div>
        <div><label className="text-xs">Pilih Kelas</label>
          <select className="imsyaf-input" value={filter.kelas_id} onChange={(e) => setFilter({ kamar_id: "", kelas_id: e.target.value, halaqah_id: "" })} data-testid="bulk-kelas">
            <option value="">— Semua Kelas —</option>
            {master.kelas.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
          </select></div>
        <div><label className="text-xs">Pilih Halaqah</label>
          <select className="imsyaf-input" value={filter.halaqah_id} onChange={(e) => setFilter({ kamar_id: "", kelas_id: "", halaqah_id: e.target.value })} data-testid="bulk-halaqah">
            <option value="">— Semua Halaqah —</option>
            {master.halaqah.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
          </select></div>
        <div className="flex items-end">
          <button onClick={markAllHadir} className="imsyaf-btn-outline w-full" data-testid="bulk-all-hadir">
            <CheckCircle size={14}/> Tandai Semua {humanize(statuses[0] || "hadir")}
          </button>
        </div>
      </div>

      <div className="imsyaf-card p-3 flex items-center justify-between flex-wrap gap-2 bg-green-50/50 sticky top-16 z-10">
        <div className="text-sm text-slate-700"><b>{santriList.length}</b> santri ditampilkan · <b>{filledCount}</b> ditandai</div>
        <button disabled={saving || filledCount === 0} onClick={submit} className="imsyaf-btn-primary disabled:opacity-50" data-testid="bulk-save-btn">
          <FloppyDisk size={16}/> {saving ? "Menyimpan…" : `Simpan Semua (${filledCount})`}
        </button>
      </div>

      <div className="imsyaf-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-3 py-2 w-10">#</th>
            <th className="text-left px-3 py-2">Nama Santri</th>
            <th className="text-left px-3 py-2 hidden md:table-cell">Kamar</th>
            <th className="text-left px-3 py-2 hidden md:table-cell">Kelas</th>
            <th className="text-center px-3 py-2">Status Kehadiran</th>
          </tr></thead>
          <tbody>
            {santriList.map((s, i) => {
              const m = marks[s.id] || {};
              const applicableStatuses = statuses.filter((st) =>
                !femaleOnlyStatuses.includes(st) || s.jenis_kelamin === "P"
              );
              return (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`bulk-row-${s.nis}`}>
                  <td className="px-3 py-2 text-slate-400 text-xs">{i + 1}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-[#064E3B]">{s.nama}</div>
                    <div className="text-[11px] font-mono text-slate-500">{s.nis} · {s.jenis_kelamin === "P" ? "Pi" : "Pa"}</div>
                  </td>
                  <td className="px-3 py-2 text-xs hidden md:table-cell">{s.kamar_nama || "-"}</td>
                  <td className="px-3 py-2 text-xs hidden md:table-cell">{s.kelas_nama || "-"}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center justify-center gap-1">
                      {applicableStatuses.map((st) => (
                        <button
                          key={st}
                          onClick={() => setMark(s.id, { status: st, ...(st !== izinKey ? { alasan: "", alasan_pick: "" } : {}) })}
                          className={`px-2.5 py-1 rounded-md text-white text-xs font-medium transition ${
                            m.status === st ? (COLORS[st] || "bg-slate-700") : "bg-slate-300 hover:bg-slate-400"
                          }`}
                          data-testid={`bulk-${s.nis}-${st}`}
                        >
                          {humanize(st)}
                        </button>
                      ))}
                    </div>
                    {m.status === izinKey && (
                      <div className="mt-2 grid grid-cols-2 gap-1">
                        <select
                          className="imsyaf-input !py-1 text-xs"
                          value={m.alasan_pick || ""}
                          onChange={(e) => setMark(s.id, { alasan_pick: e.target.value })}
                          data-testid={`bulk-${s.nis}-reason-pick`}
                        >
                          <option value="">Pilih alasan izin…</option>
                          {REASON_PRESETS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        {m.alasan_pick === "Lainnya" && (
                          <input
                            className="imsyaf-input !py-1 text-xs"
                            placeholder="Keterangan…"
                            value={m.alasan || ""}
                            onChange={(e) => setMark(s.id, { alasan: e.target.value })}
                            data-testid={`bulk-${s.nis}-reason-text`}
                          />
                        )}
                        <input
                          className="imsyaf-input !py-1 text-xs col-span-2"
                          placeholder="Catatan tambahan (opsional)"
                          value={m.catatan || ""}
                          onChange={(e) => setMark(s.id, { catatan: e.target.value })}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {santriList.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-slate-400">Pilih Kamar / Kelas / Halaqah di atas untuk menampilkan daftar santri.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
